import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Button, Card, CardHeader } from "./primitives";
import { api } from "@/lib/api";
import { isAdmin } from "@/lib/auth";
import type { Service } from "@/lib/types";

export function ServiceRegions({ service }: { service: Service }) {
  let supported = false;
  try {
    supported = service.source === "rss" &&
      ["status.aws.amazon.com", "health.aws.amazon.com"].includes(new URL(service.sourceUrl).hostname);
  } catch { /* Providers without a public feed cannot select regions. */ }
  if (!supported || !isAdmin()) return null;
  return <RegionEditor serviceId={service.id} />;
}

function RegionEditor({ serviceId }: { serviceId: string }) {
  const client = useQueryClient();
  const queryKey = ["service-regions", serviceId];
  const query = useQuery({ queryKey, queryFn: () => api.getServiceRegions(serviceId) });
  const [draft, setDraft] = useState<string[] | null>(null);
  const [specific, setSpecific] = useState<boolean | null>(null);
  const [search, setSearch] = useState("");
  const selected = draft ?? query.data?.regions ?? [];
  const scoped = specific ?? selected.length > 0;
  const save = useMutation({
    mutationFn: (regions: string[]) => api.updateServiceRegions(serviceId, regions),
    onSuccess: async (settings) => {
      client.setQueryData(queryKey, settings);
      setDraft(null);
      setSpecific(null);
      await client.invalidateQueries({ queryKey: ["snapshot"] });
    },
  });
  const next = scoped ? selected : [];
  const changed = JSON.stringify([...next].sort()) !== JSON.stringify(query.data?.regions ?? []);

  return (
    <Card>
      <CardHeader title="Monitored regions" description="Only flag AWS incidents in the regions you use." />
      <div style={{ padding: 16, display: "grid", gap: 14, fontSize: 13 }}>
        <p style={{ margin: 0, color: "var(--muted-foreground)" }}>
          Applies to dashboard status, incident counts, uptime, and email alerts for everyone.
          Global incidents and incidents without a known region are always included.
        </p>
        {query.isPending && <p role="status">Loading regions…</p>}
        {query.isError && <div role="alert">Couldn’t load regions. <Button size="sm" onClick={() => void query.refetch()}>Retry</Button></div>}
        {query.data && (
          <>
            <fieldset disabled={save.isPending} style={{ border: 0, padding: 0, margin: 0, display: "grid", gap: 12 }}>
              <legend style={{ fontWeight: 600, marginBottom: 10 }}>Monitoring scope</legend>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 20 }}>
                <label><input type="radio" name={`scope-${serviceId}`} checked={!scoped} onChange={() => { setSpecific(false); save.reset(); }} /> All regions</label>
                <label><input type="radio" name={`scope-${serviceId}`} checked={scoped} onChange={() => { setSpecific(true); save.reset(); }} /> Selected regions</label>
              </div>
              {scoped && (
                <>
                  <label style={{ display: "grid", gap: 6 }}>
                    Search regions
                    <input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="e.g. us-west-2" style={{ padding: "8px 10px", border: "1px solid var(--border)", borderRadius: 6, background: "var(--background)", color: "var(--foreground)", maxWidth: 320 }} />
                  </label>
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(170px, 1fr))", gap: 8, maxHeight: 200, overflowY: "auto", padding: 2 }}>
                    {query.data.availableRegions.filter((region) => region.includes(search.trim().toLowerCase())).map((region) => (
                      <label key={region} style={{ display: "flex", alignItems: "center", gap: 6, padding: "6px 8px", borderRadius: 5, background: selected.includes(region) ? "var(--muted)" : "transparent", fontFamily: "var(--font-mono)", fontSize: 12 }}>
                        <input type="checkbox" checked={selected.includes(region)} onChange={(event) => {
                          setSpecific(true);
                          setDraft(event.target.checked ? [...selected, region] : selected.filter((r) => r !== region));
                          save.reset();
                        }} />
                        {region}
                      </label>
                    ))}
                  </div>
                  <span style={{ color: "var(--muted-foreground)" }}>
                    {selected.length ? `Selected: ${[...selected].sort().join(", ")}` : "Select at least one region."}
                  </span>
                </>
              )}
            </fieldset>
            <div style={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 12 }}>
              <Button variant="primary" disabled={save.isPending || !changed || (scoped && !selected.length)} onClick={() => save.mutate(next)}>
                {save.isPending ? "Saving…" : "Save regions"}
              </Button>
              {save.isSuccess && <span role="status">Regions saved. Dashboard updated.</span>}
              {save.isError && <span role="alert">Couldn’t save regions. Your changes are still here; try again.</span>}
            </div>
          </>
        )}
      </div>
    </Card>
  );
}
